/** Current user session. */
import { create } from 'zustand';
import type { AccountSuspensionNotice, User } from '@apos/shared';
import { api, isApiError, setSuspendedHandler, setUnauthorizedHandler, suspensionOf, type RegisterInput } from '../lib/api';
import { compass } from '../lib/compass';
import { getNative } from '../lib/native';
import { activatePrefs } from '../lib/prefs';
import { useAccount } from './account';

/** `suspended`: the account is suspended service-wide (403 account_suspended); see SuspendedScreen. */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous' | 'offline' | 'suspended';

interface SessionState {
  user: User | null;
  status: SessionStatus;
  /** Set while status is `suspended`: the reason category, dates and support contact (never operator detail). */
  suspension: AccountSuspensionNotice | null;
  load(): Promise<void>;
  login(email: string, password: string): Promise<User>;
  /** Resolves to the mission joined via invite code, if any. */
  register(input: RegisterInput): Promise<{ user: User; joinedMissionId: string | null }>;
  logout(): Promise<void>;
}

export const useSession = create<SessionState>((set) => ({
  user: null,
  status: 'unknown',
  suspension: null,
  async load() {
    try {
      const { user } = await api.auth.me();
      await activatePrefs(user.id);
      set({ user, status: 'authenticated' });
      compass.restore();
      void useAccount.getState().load();
    } catch (e) {
      const suspension = suspensionOf(e);
      if (suspension) enterSuspended(suspension);
      else if (isApiError(e) && e.isNetwork) set({ status: 'offline' });
      else set({ user: null, status: 'anonymous', suspension: null });
    }
  },
  async login(email, password) {
    const { user } = await api.auth.login(email, password);
    await activatePrefs(user.id);
    set({ user, status: 'authenticated' });
    compass.restore();
    void useAccount.getState().load();
    return user;
  },
  async register(input) {
    const { user, joinedMissionId } = await api.auth.register(input);
    await activatePrefs(user.id);
    set({ user, status: 'authenticated' });
    compass.restore();
    void useAccount.getState().load();
    return { user, joinedMissionId: joinedMissionId ?? null };
  },
  async logout() {
    try {
      await api.auth.logout();
    } finally {
      set({ user: null, status: 'anonymous', suspension: null });
      void activatePrefs(null);
      clearOfflineCaches();
    }
  },
}));

/** Leave every signed-in view: drop cached account data and show the suspension screen. */
function enterSuspended(suspension: AccountSuspensionNotice): void {
  const wasSuspended = useSession.getState().status === 'suspended';
  useSession.setState({ user: null, status: 'suspended', suspension });
  if (!wasSuspended) {
    clearOfflineCaches();
    void activatePrefs(null);
  }
}

setSuspendedHandler((notice) => {
  if (notice) enterSuspended(notice);
  else void useSession.getState().load();
});

setUnauthorizedHandler(() => {
  if (useSession.getState().status === 'authenticated') useSession.setState({ user: null, status: 'anonymous' });
  clearOfflineCaches();
  void activatePrefs(null);
});

/** Drop the service worker's cached app data (mission, session) — nothing lingers after sign-out. */
function clearOfflineCaches(): void {
  compass.reset();
  useAccount.getState().reset();
  getNative()?.clearAccountData?.();
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'clear' });
  } catch {
    /* no service worker */
  }
}
